import React from 'react';

const CourseCertificate = ({ candidateName, courseName, completionDate, courseId }) => {
  return (
    <div className="certificate-container bg-white p-12 max-w-4xl mx-auto shadow-2xl">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Montserrat:wght@300;400;600&display=swap');
        
        .certificate-container {
          font-family: 'Montserrat', sans-serif;
          border: 20px solid #1e3a8a;
          position: relative;
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
        }
        
        .certificate-border-inner {
          border: 3px solid #14b8a6;
          padding: 50px;
          position: relative;
        }
        
        .certificate-corner {
          position: absolute;
          width: 80px;
          height: 80px;
          border: 4px solid #d4af37;
        }
        
        .corner-tl { top: -4px; left: -4px; border-right: none; border-bottom: none; }
        .corner-tr { top: -4px; right: -4px; border-left: none; border-bottom: none; }
        .corner-bl { bottom: -4px; left: -4px; border-right: none; border-top: none; }
        .corner-br { bottom: -4px; right: -4px; border-left: none; border-top: none; }
        
        .certificate-header {
          text-align: center;
          margin-bottom: 40px;
        }
        
        .certificate-logo {
          font-family: 'Playfair Display', serif;
          font-size: 52px;
          font-weight: 700;
          color: #1e293b;
          letter-spacing: 4px;
          margin-bottom: 8px;
        }
        
        .certificate-tagline {
          font-size: 12px;
          color: #94a3b8;
          letter-spacing: 3px;
          text-transform: uppercase;
          font-weight: 400;
        }
        
        .certificate-title {
          font-family: 'Playfair Display', serif;
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          letter-spacing: 6px;
          margin: 35px 0 15px;
          text-transform: uppercase;
        }
        
        .certificate-subtitle {
          font-size: 16px;
          color: #64748b;
          letter-spacing: 1px;
          font-style: italic;
          margin-bottom: 35px;
        }
        
        .certificate-text {
          font-size: 15px;
          color: #475569;
          line-height: 1.8;
          margin: 15px 0;
        }
        
        .certificate-recipient {
          font-family: 'Playfair Display', serif;
          font-size: 52px;
          font-weight: 700;
          color: #1e293b;
          margin: 25px 0;
          border-bottom: 3px solid #d4af37;
          padding-bottom: 12px;
          display: inline-block;
        }
        
        .certificate-course {
          font-family: 'Playfair Display', serif;
          font-size: 28px;
          font-weight: 600;
          color: #1e293b;
          margin: 30px 0;
        }
        
        .certificate-date {
          font-size: 15px;
          color: #64748b;
          margin-top: 35px;
          font-weight: 500;
        }
        
        .certificate-signature {
          margin-top: 60px;
          display: flex;
          justify-content: space-around;
          align-items: flex-end;
          padding: 0 40px;
        }
        
        .signature-line {
          text-align: center;
          min-width: 220px;
        }
        
        .signature-image {
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 10px;
        }
        
        .signature-image img {
          max-height: 50px;
          width: auto;
        }
        
        .signature-name {
          font-family: 'Playfair Display', serif;
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          border-top: 2px solid #1e293b;
          padding-top: 12px;
          margin-top: 5px;
        }
        
        .signature-title {
          font-size: 11px;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-top: 4px;
          font-weight: 500;
        }
        
        .certificate-id {
          position: absolute;
          bottom: 25px;
          left: 50px;
          font-size: 9px;
          color: #94a3b8;
          font-family: 'Courier New', monospace;
        }
        
        @media print {
          .certificate-container {
            border: 20px solid #1e3a8a;
            box-shadow: none;
          }
        }
      `}</style>
      
      <div className="certificate-border-inner">
        {/* Corner Decorations */}
        <div className="certificate-corner corner-tl"></div>
        <div className="certificate-corner corner-tr"></div>
        <div className="certificate-corner corner-bl"></div>
        <div className="certificate-corner corner-br"></div>
        
        {/* Header */}
        <div className="certificate-header">
          <div className="certificate-logo">APTIRIX</div>
          <div className="certificate-tagline">India's First AI-Powered Career Platform</div>
        </div>
        
        {/* Title */}
        <div className="text-center">
          <div className="certificate-title">Certificate of Completion</div>
          <div className="certificate-subtitle">Course Achievement</div>
        </div>
        
        {/* Body */}
        <div className="text-center mt-6">
          <div className="certificate-text">This is to certify that</div>
          
          <div className="certificate-recipient">{candidateName}</div>
          
          <div className="certificate-text mt-6">
            has successfully completed the course
          </div>
          
          <div className="certificate-course">{courseName}</div>
          
          <div className="certificate-text mt-6">
            demonstrating dedication, commitment, and proficiency in the subject matter.
            <br />
            This achievement reflects their pursuit of excellence and continuous learning.
          </div>
          
          <div className="certificate-date mt-8">
            <strong>Date of Completion:</strong> {new Date(completionDate).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </div>
        
        {/* Signatures */}
        <div className="certificate-signature">
          <div className="signature-line">
            <div className="signature-image">
              <img src="/signatures/naveen-signature.svg" alt="Naveen Dhanasekaran Signature" />
            </div>
            <div className="signature-name">Naveen Dhanasekaran</div>
            <div className="signature-title">CEO & Founder</div>
            <div className="signature-title">Aptirix</div>
          </div>
          
          <div className="signature-line">
            <div className="signature-image">
              <img src="/signatures/rahul-signature.svg" alt="Rahul V Signature" />
            </div>
            <div className="signature-name">Rahul V</div>
            <div className="signature-title">Co-Founder & CTO</div>
            <div className="signature-title">Aptirix</div>
          </div>
        </div>
        
        {/* Certificate ID */}
        <div className="certificate-id">
          Certificate ID: {courseId}-{new Date(completionDate).getTime()}
        </div>
      </div>
    </div>
  );
};

export default CourseCertificate;
